// FILE: src/Manager/ManagerDashboard.js
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  Suspense,
} from "react";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import { format, isPast, parseISO } from "date-fns";
import "./ManagerDashboard.css";
import api from "../services/api";

// Register chart.js components (safe to call at module load)
ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

// Lazy-load the Bar component from react-chartjs-2 to reduce initial bundle cost
const LazyBar = React.lazy(() =>
  import("react-chartjs-2").then((mod) => ({ default: mod.Bar }))
);

console.log(api.defaults.baseURL)
// API endpoint (use your real URL)
const API_MANAGER_FORMS_URL = api.defaults.baseURL + "/manager/assigned-forms";

/* ----------------------
   Helpers: parsing + preprocess
   ---------------------- */
const safeParseISO = (s) => {
  try {
    return s ? parseISO(s) : null;
  } catch {
    return null;
  }
};

const preprocessForms = (rawForms = []) => {
  return rawForms.map((f) => {
    const parsedCreatedAt = safeParseISO(f.createdAt);
    const parsedExpiresAt = safeParseISO(f.expiresAt || f.dueDate);
    const latestSubmission = f.submissions?.[f.submissions.length - 1];
    const latestTotal = Number(latestSubmission?.formData?.grandTotal) || 0;
    // expired if expiry date passed AND not finalized/submitted
    const expiredRaw = parsedExpiresAt ? isPast(parsedExpiresAt) : false;
    const expired = expiredRaw && f.status !== "finalized" && f.status !== "submitted";

    return {
      ...f,
      _parsedCreatedAt: parsedCreatedAt,
      _parsedExpiresAt: parsedExpiresAt,
      _latestTotal: latestTotal,
      _expired: expired,
    };
  });
};

/* ----------------------
   Small memoized UI components
   ---------------------- */
const KpiCard = React.memo(({ title, value, icon, description }) => {
  return (
    <div className="kpi-card">
      <div className="kpi-icon">{icon}</div>
      <div className="kpi-content">
        <div className="kpi-value">{value}</div>
        <div className="kpi-title">{title}</div>
        <p className="kpi-description">{description}</p>
      </div>
    </div>
  );
});

const BrokerPerformanceChart = React.memo(({ performanceData }) => {
  const chartData = useMemo(() => {
    return {
      labels: performanceData.map((p) => p.name),
      datasets: [
        {
          label: "Total Submitted Value ($)",
          data: performanceData.map((p) => p.totalValue),
          backgroundColor: "rgba(33, 150, 243, 0.6)",
          borderColor: "rgba(33, 150, 243, 1)",
          borderWidth: 1,
        },
      ],
    };
  }, [performanceData]);

  const options = useMemo(
    () => ({
      indexAxis: "y",
      responsive: true,
      plugins: {
        legend: { display: false },
        title: { display: true, text: "Broker Performance by Submitted Value" },
      },
      scales: {
        x: {
          beginAtZero: true,
          ticks: {
            callback: (value) =>
              new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
                notation: "compact",
              }).format(value),
          },
        },
      },
    }),
    []
  );

  return (
    <div style={{ minHeight: 200 }}>
      <Suspense fallback={<div>Loading chart...</div>}>
        <LazyBar data={chartData} options={options} />
      </Suspense>
    </div>
  );
});

/* ----------------------
   Main Component
   ---------------------- */
const ManagerDashboard = () => {
  // Data + pagination
  const [forms, setForms] = useState([]); // preprocessed forms
  const [page, setPage] = useState(1);
  const LIMIT = 25; // page size — tune as needed
  const [hasMore, setHasMore] = useState(true);

  // Loading & errors
  const [isLoading, setIsLoading] = useState(true); // initial load
  const [isLoadingMore, setIsLoadingMore] = useState(false); // subsequent pages
  const [error, setError] = useState(null);

  // UI state
  const [rawSearchTerm, setRawSearchTerm] = useState("");
  const [searchTerm, setSearchTerm] = useState(""); // debounced
  const [activeStatus, setActiveStatus] = useState("all");

  // manager name from localStorage
  const managerName = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user"))?.name || "Manager";
    } catch {
      return "Manager";
    }
  }, []);

  // Debounce rawSearchTerm -> searchTerm (300ms)
  useEffect(() => {
    const id = setTimeout(() => {
      setSearchTerm(rawSearchTerm.trim().toLowerCase());
    }, 300);
    return () => clearTimeout(id);
  }, [rawSearchTerm]);

  // Fetch with pagination. Uses AbortController to cancel stale requests.
  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    const fetchPage = async (pageToFetch) => {
      setError(null);
      if (pageToFetch === 1) {
        setIsLoading(true);
      } else {
        setIsLoadingMore(true);
      }

      const token = localStorage.getItem("authToken");
      if (!token) {
        setError("Authentication Error: Please log in again.");
        setIsLoading(false);
        setIsLoadingMore(false);
        return;
      }

      try {
        const url = `${API_MANAGER_FORMS_URL}?page=${pageToFetch}&limit=${LIMIT}`;
        const resp = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
          signal,
        });

        if (!resp.ok) {
          // If status is 401/403 handle specially
          throw new Error(`Server Error: ${resp.status}`);
        }

        const json = await resp.json();
        // If backend returns an object like { data: [...], total: N } handle it
        const rawList = Array.isArray(json) ? json : json?.data || [];

        const processed = preprocessForms(rawList);

        setForms((prev) => {
          // Avoid duplicates if items overlap between pages
          if (pageToFetch === 1) return processed;
          const existingIds = new Set(prev.map((p) => p._id));
          const merged = [...prev];
          for (const item of processed) {
            if (!existingIds.has(item._id)) merged.push(item);
          }
          return merged;
        });

        setHasMore(processed.length === LIMIT);
      } catch (e) {
        if (e.name !== "AbortError") {
          setError(e.message || "Failed to fetch data");
        }
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    };

    fetchPage(page);

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]); // only re-run when page changes

  // Utility wrapper for expired (uses precomputed field)
  const isFormExpired = useCallback((form) => !!form._expired, []);

  /* ----------------------
     Analytics (memoized)
     ---------------------- */
  const analytics = useMemo(() => {
    if (!forms || forms.length === 0) {
      return {
        totalSubmittedValue: 0,
        totalFinalizedValue: 0,
        pendingCount: 0,
        submittedCount: 0,
        finalizedCount: 0,
        reassignedCount: 0,
        expiredCount: 0,
        brokerPerformance: [],
      };
    }

    const submittedForms = forms.filter((f) => f.status === "submitted");
    const pendingForms = forms.filter((f) => f.status === "pending");
    const finalizedForms = forms.filter((f) => f.status === "finalized");
    const reassignedForms = forms.filter((f) => f.status === "reassigned");
    const expiredForms = forms.filter((f) => f._expired);

    const totalSubmittedValue = submittedForms.reduce((sum, form) => sum + (form._latestTotal || 0), 0);
    const totalFinalizedValue = finalizedForms.reduce((sum, form) => sum + (form._latestTotal || 0), 0);

    const performanceMap = submittedForms.reduce((acc, form) => {
      const brokerId = form.brokerUserId?._id;
      if (!brokerId) return acc;
      const brokerName = form.brokerUserId?.name || "Unknown Broker";
      const grandTotal = form._latestTotal || 0;
      if (!acc[brokerId]) acc[brokerId] = { name: brokerName, totalValue: 0, submissionCount: 0 };
      acc[brokerId].totalValue += grandTotal;
      acc[brokerId].submissionCount += 1;
      return acc;
    }, {});

    const brokerPerformance = Object.values(performanceMap).sort((a, b) => b.totalValue - a.totalValue);

    return {
      totalSubmittedValue,
      totalFinalizedValue,
      pendingCount: pendingForms.length,
      submittedCount: submittedForms.length,
      finalizedCount: finalizedForms.length,
      reassignedCount: reassignedForms.length,
      expiredCount: expiredForms.length,
      brokerPerformance,
    };
  }, [forms]);

  /* ----------------------
     Filtered forms (memoized)
     ---------------------- */
  const filteredForms = useMemo(() => {
    const lowerSearch = searchTerm || "";
    return forms
      .filter((form) => {
        if (activeStatus === "all") return true;
        if (activeStatus === "expired") return form._expired;
        return form.status === activeStatus;
      })
      .filter((form) => {
        if (!lowerSearch) return true;
        const brokerName = form.brokerUserId?.name || "";
        const invoiceId = form.baseInvoiceId || "";
        return `${brokerName} ${invoiceId}`.toLowerCase().includes(lowerSearch);
      });
  }, [forms, activeStatus, searchTerm]);

  // Helpers
  const formatDateObj = (d) => (d ? format(d, "MMM dd, yyyy") : "N/A");

  // Handlers
  const handleSetStatus = useCallback((s) => setActiveStatus(s), []);
  const handleLoadMore = useCallback(() => setPage((p) => p + 1), []);

  /* ----------------------
     Render
     ---------------------- */
  if (isLoading)
    return (
      <div className="manager-dashboard-page">
        <div className="card">
          <h2>Loading Dashboard...</h2>
        </div>
      </div>
    );

  if (error)
    return (
      <div className="manager-dashboard-page">
        <div className="card error-card">
          <h2>Error</h2>
          <p>{error}</p>
          <div style={{ marginTop: 8 }}>
          </div>
        </div>
      </div>
    );

  return (
    <div className="manager-dashboard-page">
      <header className="dashboard-header">
        <h1>Manager Dashboard</h1>
        <p>Welcome back, {managerName}! Here's the latest activity from your team.</p>
      </header>

      <section className="kpi-grid">
        <KpiCard
          title="Total Submitted Value"
          value={new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(analytics.totalSubmittedValue)}
          icon="💰"
          description={`From ${analytics.submittedCount} submitted forms`}
        />
        <KpiCard
          title="Total Finalized Value"
          value={new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(analytics.totalFinalizedValue)}
          icon="🏆"
          description={`From ${analytics.finalizedCount} finalized forms`}
        />
        <KpiCard title="Pending Submissions" value={analytics.pendingCount} icon="⏳" description="Forms awaiting broker action" />
        <KpiCard title="Reassigned Forms" value={analytics.reassignedCount} icon="🔄" description="Forms awaiting broker revision" />
        <KpiCard title="Expired Forms" value={analytics.expiredCount} icon="🚨" description="Forms past their due date" />
        <KpiCard title="Total Finalized Forms" value={analytics.finalizedCount} icon="✔️" description="Forms that have been completed" />
      </section>

      <main className="dashboard-layout">
        <div className="dashboard-main-content">
          <div className="card">
            <div className="table-header">
              <h3 className="card-title">All Assigned Forms</h3>
              <div className="table-controls">
                <input
                  type="text"
                  placeholder="Search by Broker or Invoice..."
                  className="search-bar"
                  value={rawSearchTerm}
                  onChange={(e) => setRawSearchTerm(e.target.value)}
                />
                <div className="filter-buttons" style={{ display: "flex", gap: 6 }}>
                  <button onClick={() => handleSetStatus("all")} className={activeStatus === "all" ? "active" : ""}>All</button>
                  <button onClick={() => handleSetStatus("pending")} className={activeStatus === "pending" ? "active" : ""}>Pending</button>
                  <button onClick={() => handleSetStatus("submitted")} className={activeStatus === "submitted" ? "active" : ""}>Submitted</button>
                  <button onClick={() => handleSetStatus("reassigned")} className={activeStatus === "reassigned" ? "active" : ""}>Reassigned</button>
                  <button onClick={() => handleSetStatus("finalized")} className={activeStatus === "finalized" ? "active" : ""}>Finalized</button>
                  <button onClick={() => handleSetStatus("expired")} className={activeStatus === "expired" ? "active" : ""}>Expired</button>
                </div>
                <div style={{ marginLeft: 12 }}>
                </div>
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Broker</th>
                    <th>Status</th>
                    <th>Invoice #</th>
                    <th>Value</th>
                    <th>Assigned On</th>
                    <th>Expired</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredForms.map((form) => {
                    const grandTotal = form._latestTotal || 0;
                    const isCurrentFormExpired = isFormExpired(form);
                    let displayStatus = form.status;
                    if (isCurrentFormExpired && activeStatus === "expired") displayStatus = "expired";

                    return (
                      <tr key={form._id}>
                        <td>{form.brokerUserId?.name || "N/A"}</td>
                        <td>
                          <span className={`badge status-${displayStatus}`}>{displayStatus}</span>
                        </td>
                        <td>{form.baseInvoiceId || "---"}</td>
                        <td>
                          {form.status === "submitted" || form.status === "finalized"
                            ? new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(grandTotal)
                            : "---"}
                        </td>
                        <td>{formatDateObj(form._parsedCreatedAt)}</td>
                        <td className={isCurrentFormExpired ? "expired-date" : ""}>{formatDateObj(form._parsedExpiresAt)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <div style={{ padding: 12, display: "flex", justifyContent: "center", alignItems: "center" }}>
                {isLoadingMore ? (
                  <button disabled>Loading more...</button>
                ) : hasMore ? (
                  <button onClick={handleLoadMore}>Load more</button>
                ) : (
                  <small>No more records</small>
                )}
              </div>
            </div>
          </div>
        </div>

        <aside className="dashboard-sidebar-content">
          <div className="card">
            <h3 className="card-title">Broker Performance</h3>
            {analytics.brokerPerformance.length > 0 ? (
              <BrokerPerformanceChart performanceData={analytics.brokerPerformance} />
            ) : (
              <p className="empty-state">No submitted forms to analyze.</p>
            )}
          </div>
        </aside>
      </main>
    </div>
  );
};

export default ManagerDashboard;
